import { Guid } from "client_legacy/ndaw/guid";
import { ReactElement, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { isNil } from "client/Misc/utils";
import {
	ActionListItem,
	DropOverActionList
} from "client/Components";
import { never } from "client_legacy/ndaw/errors";
import { GroupItemFormModel } from "./common";
import { GroupSelectionMenu } from "./GroupSelectionMenu";
import { OpenItemMenuState, useItemDropOverMenuProps } from "client/Hooks";

export type ScopeItemMenuProps = {
	target: HTMLElement;
	onClose: () => void;
	menuState: OpenItemMenuState;
	name: string;
	containingGroupId?: Guid;
	groups: readonly GroupItemFormModel[];
	onMoveToGroup?: (targetGroupId: Guid) => void;
	onMoveToTop?: () => void;
	onRemove?: () => void;
};

export const ScopeItemMenu = (props: ScopeItemMenuProps): ReactElement => {
	const {
		target,
		onClose,
		menuState,
		name,
		containingGroupId,
		groups,
		onMoveToGroup,
		onMoveToTop,
		onRemove
	} = props;

	const [view, setView] = useState<View>("main");

	const {
		closeOnMouseDown,
		contentAlignment,
		targetAlignment
	} = useItemDropOverMenuProps(
		menuState,
		{
			moreMenuTargetAlignment: "br",
			moreMenuContentAlignment: "tr"
		}
	);

	const moveToGroupTitle: string = localize(
		isNil(containingGroupId)
			? "_MoveToGroup"
			: "_MoveToAnotherGroup"
	);

	const renderMenu = (): ReactElement | null => {
		switch(view) {
			case "main":
				return (
					<>
						{onMoveToTop && (
							<ActionListItem
								onClick={onMoveToTop}
								text={localize("_MoveOutOfGroup")}
								trackingId="move-out-of-group"
								icon="arrow-up-to-line"
							/>
						)}
						{(groups.length > 0 && onMoveToGroup) && (
							<ActionListItem
								onClick={() => setView("group-selection")}
								text={moveToGroupTitle}
								trackingId="move-to-another-group"
								icon="arrow-right-to-line"
								rightIcon="chevron-right"
								shouldNotAutoCloseContainingDropOver
							/>
						)}
						{onRemove && (
							<ActionListItem
								onClick={onRemove}
								text={localize("_Remove")}
								trackingId="remove-scope"
								icon="trash-can"
							/>
						)}
					</>
				);
			case "group-selection":
				return onMoveToGroup
					? (
						<GroupSelectionMenu
							containingGroupId={containingGroupId}
							groups={groups}
							moveToGroupTitle={moveToGroupTitle}
							onMoveToGroup={onMoveToGroup}
							onBack={() => setView("main")}
						/>
					)
					: null;
			default:
				never(view);
		}
	};

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			menuTitle={name}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			closeOnMouseDown={closeOnMouseDown}
		>
			{renderMenu()}
		</DropOverActionList>
	);
};
ScopeItemMenu.displayName = "ScopeItemMenu";

type View = "main" | "group-selection";